import { useEffect, useMemo, useRef, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import Table from "react-bootstrap/Table";
import Select from "react-select";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import CashbookServices from "../../../services/CashbookServices";
import CashBookReportServices from "../../../services/CashBookReportServices";

import "./CashBookPrint.css";

const reportTypeOptions = [
  { value: "all", label: "All Entries" },
  { value: "dayWise", label: "Day-wise" },
  { value: "partyWise", label: "Party-wise" },
  { value: "categoryWise", label: "Category-wise" },
  { value: "paymentModeWise", label: "Payment Mode-wise" }
];

const readData = (response) => {
  const body = response?.data?.status ? response.data : response;
  return body?.data;
};

const formatCategory = (value) =>
  String(value || "Unnamed Category")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const numericAmount = (value) => {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    throw new Error(
      "The report contains a missing amount. Please check the updated report API."
    );
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    throw new Error("The report contains an invalid amount.");
  }

  return amount;
};

const formatAmount = (value) => {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return "—";
  }

  const amount = Number(value);

  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : "—";
};

const formatDate = (value) => {
  if (!value) return "—";

  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

// Validate and normalize API amounts before displaying/exporting.
const normalizeReport = (data) => {
  if (
    data?.reportType !== "categoryWise" ||
    !Array.isArray(data.rows) ||
    !data.totals ||
    !data.openingBalanceDate
  ) {
    throw new Error("Unexpected report API response.");
  }

  const normalizeAmounts = (row) => {
    const openingBalance = numericAmount(row.openingBalance);
    const cashIn = numericAmount(row.cashIn);
    const cashOut = numericAmount(row.cashOut);
    const suppliedNetBalance = numericAmount(row.netBalance);

    const netMovement = cashIn - cashOut;
    const netBalance = openingBalance + netMovement;

    if (Math.abs(suppliedNetBalance - netBalance) > 0.005) {
      throw new Error(
        "Report balance mismatch: Net Balance must equal Opening Balance + Cash In − Cash Out."
      );
    }

    return {
      ...row,
      openingBalance,
      cashIn,
      cashOut,
      netMovement,
      netBalance,
      closingBalance: netBalance,
      entryCount: numericAmount(row.entryCount)
    };
  };

  return {
    ...data,
    rows: data.rows.map(normalizeAmounts),
    totals: normalizeAmounts(data.totals)
  };
};

const ExcelIcon = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
      fill="#E7F3EC"
      stroke="#217346"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 2v6h6"
      stroke="#217346"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <rect
      x="1"
      y="10"
      width="14"
      height="11"
      rx="2"
      fill="#217346"
    />
    <path
      d="m5 13 6 5m0-5-6 5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ToggleButton = ({ open, onClick, controls, label }) => (
  <button
    type="button"
    className="cashbook-toggle"
    onClick={onClick}
    aria-label={label}
    aria-expanded={open}
    aria-controls={controls}
  >
    <i
      className={`fas ${open ? "fa-angle-up" : "fa-angle-down"}`}
      aria-hidden="true"
    />
  </button>
);

const CashBookPrint = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedPassbook, setSelectedPassbook] = useState("");
  const [reportType, setReportType] = useState("categoryWise");

  const [passbooks, setPassbooks] = useState([]);
  const [passbooksLoading, setPassbooksLoading] = useState(true);
  const [passbookError, setPassbookError] = useState("");

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);
  const [resetKey, setResetKey] = useState(0);

  const searchLock = useRef(false);
  const exportLock = useRef(false);
  const mounted = useRef(false);

  const busy = reportLoading || exporting;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPassbooks = async () => {
      setPassbooksLoading(true);
      setPassbookError("");

      try {
        const response = await CashbookServices.getPassbooks();
        const data = readData(response);

        if (!Array.isArray(data?.passbooks)) {
          throw new Error("Unexpected passbook API response.");
        }

        if (!cancelled) {
          setPassbooks(data.passbooks);
        }
      } catch (error) {
        if (!cancelled) {
          setPassbooks([]);
          setPassbookError(
            error?.response?.data?.message ||
              error.message ||
              "Unable to load passbooks."
          );
        }
      } finally {
        if (!cancelled) {
          setPassbooksLoading(false);
        }
      }
    };

    loadPassbooks();

    return () => {
      cancelled = true;
    };
  }, []);

  const passbookOptions = useMemo(() => {
    const options = passbooks
      .filter((item) => item?._id)
      .map((item) => ({
        value: String(item._id),
        label:
          String(item.name || "Passbook").toUpperCase() +
          (item.status === "inactive" ? " (Inactive)" : "")
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return options.length
      ? [{ value: "all", label: "All Passbooks" }, ...options]
      : [];
  }, [passbooks]);

  const invalidDateRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  const canSearch =
    !passbooksLoading &&
    !busy &&
    !passbookError &&
    Boolean(fromDate) &&
    Boolean(toDate) &&
    Boolean(selectedPassbook) &&
    !invalidDateRange;

  const clearPreview = () => {
    setReport(null);
    setReportError("");
  };

  const resetAll = () => {
    if (searchLock.current || exportLock.current) return;

    setFromDate("");
    setToDate("");
    setSelectedPassbook("");
    setReportType("categoryWise");
    setResetKey((previous) => previous + 1);
    clearPreview();
  };

  const handleSearch = async () => {
    if (
      !canSearch ||
      searchLock.current ||
      exportLock.current
    ) {
      return;
    }

    setOpen2(true);
    clearPreview();

    if (reportType !== "categoryWise") {
      setReportError(
        "Only Category-wise report is available currently."
      );
      return;
    }

    searchLock.current = true;
    setReportLoading(true);

    try {
      const response =
        await CashBookReportServices.getCategoryWiseReport({
          fromDate,
          toDate,
          passbookId: selectedPassbook
        });

      const data = normalizeReport(readData(response));

      if (mounted.current) {
        setReport(data);
      }
    } catch (error) {
      if (mounted.current) {
        setReportError(
          error?.response?.data?.message ||
            error.message ||
            "Unable to load report."
        );
      }
    } finally {
      searchLock.current = false;

      if (mounted.current) {
        setReportLoading(false);
      }
    }
  };

  const handleExcelDownload = async () => {
    if (
      !report ||
      reportLoading ||
      searchLock.current ||
      exportLock.current
    ) {
      return;
    }

    exportLock.current = true;
    setExporting(true);

    const currentReport = report;

    try {
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (!mounted.current) return;

      const passbookName = String(
        currentReport.passbookName || "All Passbooks"
      ).toUpperCase();

      const sheetData = [
        ["INDIGO INFRAPROJECTS PVT. LTD."],
        ["CASHBOOK CATEGORY-WISE REPORT"],
        ["Passbook", passbookName],
        [
          "Duration",
          `${formatDate(currentReport.fromDate)} to ${formatDate(
            currentReport.toDate
          )}`
        ],
        [
          "Opening As Of",
          `${formatDate(currentReport.openingBalanceDate)} — end of day (IST)`
        ],
        [
          "Report Basis",
          "Approved entries only. Net Balance = Opening Balance + Cash In - Cash Out."
        ],
        [],
        [
          "Category",
          "Opening Balance",
          "Cash In",
          "Cash Out",
          "Net Balance"
        ],
        ...currentReport.rows.map((row) => [
          formatCategory(row.category),
          numericAmount(row.openingBalance),
          numericAmount(row.cashIn),
          numericAmount(row.cashOut),
          numericAmount(row.netBalance)
        ]),
        [
          "Total",
          numericAmount(currentReport.totals.openingBalance),
          numericAmount(currentReport.totals.cashIn),
          numericAmount(currentReport.totals.cashOut),
          numericAmount(currentReport.totals.netBalance)
        ]
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      worksheet["!cols"] = [
        { wch: 45 },
        { wch: 24 },
        { wch: 24 },
        { wch: 24 },
        { wch: 24 }
      ];

      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        ...[2, 3, 4, 5].map((row) => ({
          s: { r: row, c: 1 },
          e: { r: row, c: 4 }
        }))
      ];

      const totalRow = 8 + currentReport.rows.length;

      for (let row = 8; row <= totalRow; row += 1) {
        for (let column = 1; column <= 4; column += 1) {
          const address = XLSX.utils.encode_cell({
            r: row,
            c: column
          });

          if (worksheet[address]) {
            worksheet[address].z = "#,##0.00";
          }
        }
      }

      if (currentReport.rows.length > 0) {
        worksheet["!autofilter"] = {
          ref: XLSX.utils.encode_range({
            s: { r: 7, c: 0 },
            e: { r: totalRow - 1, c: 4 }
          })
        };
      }

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Category-wise"
      );

      const safeName =
        passbookName
          .replace(/[^a-zA-Z0-9_-]+/g, "_")
          .replace(/^_+|_+$/g, "")
          .slice(0, 70) || "CASHBOOK";

      XLSX.writeFile(
        workbook,
        `CASHBOOK_CATEGORY_WISE_${safeName}_${currentReport.fromDate}_TO_${currentReport.toDate}.xlsx`,
        {
          bookType: "xlsx",
          compression: true
        }
      );

      if (mounted.current) {
        void Swal.fire({
          icon: "success",
          title: "Excel ready",
          text: "Your Excel download has started.",
          timer: 2200,
          showConfirmButton: false
        });
      }
    } catch (error) {
      if (mounted.current) {
        void Swal.fire({
          icon: "error",
          title: "Download failed",
          text: error.message || "Unable to generate Excel."
        });
      }
    } finally {
      exportLock.current = false;

      if (mounted.current) {
        setExporting(false);
      }
    }
  };

  return (
    <div className="col-xl-12 cashbook-print">
      <div className="filter cm-content-box box-primary">
        <div className="content-title">
          <div className="cpa">
            <i className="fa fa-book me-2" aria-hidden="true" />
            CashBook Print
          </div>

          <div className="tools">
            <ToggleButton
              open={open}
              onClick={() => setOpen((value) => !value)}
              controls="cashbook-search-panel"
              label="Toggle search"
            />
          </div>
        </div>

        <Collapse in={open}>
          <div
            id="cashbook-search-panel"
            className="cm-content-body form excerpt"
          >
            <div className="card-body" key={resetKey}>
              <div className="row g-2 align-items-end cashbook-filters">
                <div className="col-12 col-md-6 col-xl-2">
                  <label
                    htmlFor="cashbook-from-date"
                    className="form-label mb-1"
                  >
                    From Date
                  </label>

                  <input
                    id="cashbook-from-date"
                    type="date"
                    className="form-control"
                    value={fromDate}
                    max={toDate || undefined}
                    disabled={busy}
                    aria-invalid={invalidDateRange}
                    onChange={(event) => {
                      setFromDate(event.target.value);
                      clearPreview();
                    }}
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-2">
                  <label
                    htmlFor="cashbook-to-date"
                    className="form-label mb-1"
                  >
                    To Date
                  </label>

                  <input
                    id="cashbook-to-date"
                    type="date"
                    className="form-control"
                    value={toDate}
                    min={fromDate || undefined}
                    disabled={busy}
                    aria-invalid={invalidDateRange}
                    onChange={(event) => {
                      setToDate(event.target.value);
                      clearPreview();
                    }}
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-3">
                  <label
                    htmlFor="cashbook-passbook"
                    className="form-label mb-1"
                  >
                    Passbook
                  </label>

                  <Select
                    inputId="cashbook-passbook"
                    instanceId="cashbook-passbook-select"
                    className="custom-react-select"
                    classNamePrefix="cashbook-select"
                    options={passbookOptions}
                    value={
                      passbookOptions.find(
                        (option) => option.value === selectedPassbook
                      ) || null
                    }
                    onChange={(option) => {
                      setSelectedPassbook(option?.value || "");
                      clearPreview();
                    }}
                    placeholder={
                      passbooksLoading
                        ? "Loading Passbooks..."
                        : "Search / Select Passbook"
                    }
                    isSearchable
                    isClearable
                    isLoading={passbooksLoading}
                    isDisabled={passbooksLoading || busy}
                    menuPlacement="auto"
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-2">
                  <label
                    htmlFor="cashbook-report-type"
                    className="form-label mb-1"
                  >
                    Report Type
                  </label>

                  <Select
                    inputId="cashbook-report-type"
                    instanceId="cashbook-report-type-select"
                    className="custom-react-select"
                    classNamePrefix="cashbook-select"
                    options={reportTypeOptions}
                    value={reportTypeOptions.find(
                      (option) => option.value === reportType
                    )}
                    onChange={(option) => {
                      setReportType(option?.value || "categoryWise");
                      clearPreview();
                    }}
                    isSearchable
                    isClearable={false}
                    isDisabled={busy}
                    menuPlacement="auto"
                  />
                </div>

                <div className="col-12 col-xl-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary cashbook-action-button flex-fill"
                      disabled={!canSearch}
                      onClick={handleSearch}
                    >
                      {reportLoading ? "Loading..." : "Search"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary cashbook-action-button flex-fill"
                      disabled={busy}
                      onClick={resetAll}
                    >
                      Reset All
                    </button>
                  </div>
                </div>
              </div>

              {invalidDateRange && (
                <div className="text-danger mt-2" role="alert">
                  To Date must be on or after From Date.
                </div>
              )}

              {passbookError && (
                <div className="text-danger mt-2" role="alert">
                  {passbookError}
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      <div className="filter cm-content-box box-primary">
        <div className="content-title">
          <div className="cpa">
            <i className="fa fa-list me-2" aria-hidden="true" />
            CashBook Report
          </div>

          <div className="tools cashbook-report-tools">
            {report && !reportLoading && (
              <button
                type="button"
                className="cashbook-excel-icon"
                onClick={handleExcelDownload}
                disabled={exporting}
                title="Download Excel"
                aria-label="Download Excel"
                aria-busy={exporting}
              >
                {exporting ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <ExcelIcon />
                )}
              </button>
            )}

            <ToggleButton
              open={open2}
              onClick={() => setOpen2((value) => !value)}
              controls="cashbook-report-panel"
              label="Toggle report"
            />
          </div>
        </div>

        <Collapse in={open2}>
          <div
            id="cashbook-report-panel"
            className="cm-content-body form excerpt"
          >
            <div
              className="card-body cashbook-report-body"
              aria-busy={busy}
            >
              {reportError && (
                <div className="alert alert-danger mb-3" role="alert">
                  {reportError}
                </div>
              )}

              {report && (
                <div className="cashbook-report-summary">
                  <h5 className="cashbook-report-title">
                    Category-wise Report
                  </h5>

                  <div className="row g-3">
                    <div className="col-12 col-lg-4">
                      <div className="cashbook-detail">
                        <div className="cashbook-detail-label">
                          Passbook
                        </div>
                        <div className="cashbook-detail-value">
                          {String(report.passbookName || "—").toUpperCase()}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-4">
                      <div className="cashbook-detail">
                        <div className="cashbook-detail-label">
                          Duration
                        </div>
                        <div className="cashbook-detail-value">
                          {formatDate(report.fromDate)} to{" "}
                          {formatDate(report.toDate)}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-4">
                      <div className="cashbook-detail">
                        <div className="cashbook-detail-label">
                          Report Basis
                        </div>
                        <div className="cashbook-detail-value">
                          Approved Entries Only
                        </div>
                        <div className="cashbook-detail-note">
                          {report.totals.entryCount} approved transactions
                          during the selected duration
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info mt-3 mb-0">
                    <strong>
                      Net Balance = Opening Balance + Cash In − Cash Out.
                    </strong>{" "}
                    Each category&apos;s opening balance includes approved
                    transactions through{" "}
                    {formatDate(report.openingBalanceDate)} (IST).
                    Cash In and Cash Out cover the selected duration only.
                  </div>

                  {report.totals.entryCount === 0 && (
                    <div className="text-center text-muted mt-3">
                      No approved transactions during the selected duration.
                      Opening balances are carried forward.
                    </div>
                  )}
                </div>
              )}

              <div
                className="scrolltable cashbook-table-scroll"
                role="region"
                aria-label="Category-wise cashbook report"
                tabIndex={0}
              >
                <Table bordered hover className="cashbook-report-table">
                  <caption className="visually-hidden">
                    Category-wise Opening Balance, Cash In, Cash Out
                    and Net Balance
                  </caption>

                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Opening Balance</th>
                      <th scope="col">Cash In</th>
                      <th scope="col">Cash Out</th>
                      <th scope="col">Net Balance</th>
                    </tr>
                  </thead>

                  <tbody>
                    {reportLoading ? (
                      <tr>
                        <td colSpan={5} className="cashbook-state-cell">
                          <div
                            className="cashbook-state-message"
                            role="status"
                          >
                            <span
                              className="spinner-border spinner-border-sm"
                              aria-hidden="true"
                            />
                            Loading report...
                          </div>
                        </td>
                      </tr>
                    ) : !report ? (
                      <tr>
                        <td colSpan={5} className="cashbook-state-cell">
                          <div className="cashbook-state-message">
                            {reportError
                              ? "Report could not be loaded."
                              : "Select dates, passbook and Category-wise, then click Search."}
                          </div>
                        </td>
                      </tr>
                    ) : report.rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="cashbook-state-cell">
                          No categories found.
                        </td>
                      </tr>
                    ) : (
                      report.rows.map((row, index) => (
                        <tr key={row.categoryId || `category-${index}`}>
                          <td>{formatCategory(row.category)}</td>
                          <td className="text-nowrap">
                            {formatAmount(row.openingBalance)}
                          </td>
                          <td className="text-nowrap">
                            {formatAmount(row.cashIn)}
                          </td>
                          <td className="text-nowrap">
                            {formatAmount(row.cashOut)}
                          </td>
                          <td className="text-nowrap">
                            {formatAmount(row.netBalance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  {report && !reportLoading && (
                    <tfoot>
                      <tr>
                        <th scope="row">Total</th>
                        <td className="text-nowrap">
                          {formatAmount(report.totals.openingBalance)}
                        </td>
                        <td className="text-nowrap">
                          {formatAmount(report.totals.cashIn)}
                        </td>
                        <td className="text-nowrap">
                          {formatAmount(report.totals.cashOut)}
                        </td>
                        <td className="text-nowrap">
                          {formatAmount(report.totals.netBalance)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </div>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default CashBookPrint;